// apps/web/src/widgets/scaling-matrix/compute-scaling.ts
//
// Phase 11.4 Plan 02 — the PURE reference-math module for the scaling-matrix
// renderer. Turns Plan 01's compute-scaling.test.ts (the frozen known-answer
// contract) GREEN. Four pure functions implement CONTEXT decision C; every one
// is a pure function of its arguments and surfaces a SAFE FALLBACK (`undefined`)
// on any non-finite path so the renderer can render "—" / "Yield: —" instead of
// "NaN"/"Infinity".
//
// PURITY (WR-04, copied verbatim from format-yield.ts:11-22): no DOM, no
// `console.*`, no observable side effect. The previous size-and-yield revision
// emitted `console.error` on degenerate inputs; review finding WR-04 removed it
// because expected-degenerate inputs are NOT errors — they are a documented
// `undefined` return the renderer already handles. Same discipline here: the
// renderer owns the observable signal (the "—" fallback); these helpers stay
// silent. If a richer error channel is needed later, prefer a discriminated
// return union over re-introducing a console side effect.
//
// NON-FINITE GUARD shape (copied from format-yield.ts:47-59): guard
// `!Number.isFinite(...)` at entry AND after every multiply/divide. A hostile
// recipe config (secondaryStep.multiplier of 0 → divide-by-zero → Infinity, a
// MAX_VALUE × scale overflow → Infinity, or a smuggled NaN) must fall back to
// `undefined`, never propagate Infinity/NaN to the rendered yield (threat
// T-11.4.02-01 / Pitfall 2).
//
// GENERALITY (C1): NO numeric literal magic number and NO recipe-vocabulary
// identifier lives in this file. The scale factor arrives in
// `yieldRule.primaryScale`; the per-step base fractions arrive in the resolved
// step `multiplier` values; the integer bounds arrive in `bounds.{min,max}`.
// The engine never names a recipe's vocabulary or hard-codes its constants.
//
// isolatedDeclarations (D-15): every exported function carries an explicit
// param-interface type and an explicit return type so tsc emits declarations
// without reading across module boundaries (no TS9013).

// A resolved axis STEP exposes its base-fraction `multiplier`. Pitfall 1: this
// per-step multiplier is a SEPARATE concern from the typeable stepper's
// value+bounds (the `{ min, max }` triple consumed by `clampMultiplier`).
export interface ScalingStepLike {
  multiplier: number;
}

// The recipe-declared yield rule selects how a per-pass yield is derived from
// the two resolved axis steps. `primary-over-secondary` carries a
// recipe-declared positive scale factor (`primaryScale`); the omitted/legacy
// branch is `primary-times-base`.
export type ScalingYieldRuleLike =
  | { kind: "primary-times-base" }
  | { kind: "primary-over-secondary"; primaryScale: number };

export interface PerPassYieldArgs {
  primaryStep: ScalingStepLike;
  secondaryStep: ScalingStepLike;
  yieldRule?: ScalingYieldRuleLike | undefined;
  baseValue: number;
}

// The integer-bound triple for one axis-role's typeable multiplier (the
// stepper's value+bounds — NEVER the per-step base fraction).
export interface MultiplierBoundsLike {
  min: number;
  max: number;
}

export interface EffectiveBaseEquivalentArgs {
  primaryStep: ScalingStepLike;
  multiplier: number;
  isPrimaryRole: boolean;
}

export interface SeriesLike {
  multiplierPerIndex: number;
}

/**
 * Per-pass yield from the two resolved axis steps (CONTEXT C).
 *
 * - `primary-over-secondary`:
 *     max(1, round(primaryStep.multiplier × yieldRule.primaryScale / secondaryStep.multiplier))
 *   The secondary step's multiplier is the divisor — a 0 (→ Infinity) falls back
 *   to `undefined` (Pitfall 2). max(1, …) floors the result so a large divisor
 *   never yields 0.
 * - `primary-times-base` (and the omitted/legacy default):
 *     round(baseValue × primaryStep.multiplier)
 *
 * Returns `undefined` on ANY non-finite input or intermediate (the renderer
 * surfaces "Yield: —").
 */
export function perPassYield({
  primaryStep,
  secondaryStep,
  yieldRule,
  baseValue,
}: PerPassYieldArgs): number | undefined {
  const primaryMultiplier = primaryStep.multiplier;

  // Entry guard: a non-finite primary multiplier (NaN / ±Infinity) bails before
  // any arithmetic regardless of branch.
  if (!Number.isFinite(primaryMultiplier)) {
    return undefined;
  }

  if (yieldRule !== undefined && yieldRule.kind === "primary-over-secondary") {
    const secondaryMultiplier = secondaryStep.multiplier;
    const { primaryScale } = yieldRule;

    // Entry guard: non-finite scale or divisor → fallback. A divisor of 0 is
    // finite but produces Infinity below; that is caught by the post-divide
    // guard (defense-in-depth: also reject a non-finite divisor up front).
    if (!Number.isFinite(primaryScale) || !Number.isFinite(secondaryMultiplier)) {
      return undefined;
    }

    const scaled = primaryMultiplier * primaryScale;
    // Post-multiply guard: MAX_VALUE × scale can overflow to Infinity (threat
    // T-11.4.02-01).
    if (!Number.isFinite(scaled)) {
      return undefined;
    }

    const ratio = scaled / secondaryMultiplier;
    // Post-divide guard: a 0 divisor → Infinity; a 0/0 → NaN. Both fall back.
    if (!Number.isFinite(ratio)) {
      return undefined;
    }

    return Math.max(1, Math.round(ratio));
  }

  // primary-times-base (explicit) or omitted yieldRule (legacy default).
  if (!Number.isFinite(baseValue)) {
    return undefined;
  }

  const product = baseValue * primaryMultiplier;
  // Post-multiply guard: baseValue × primaryMultiplier overflow → fallback.
  if (!Number.isFinite(product)) {
    return undefined;
  }

  return Math.round(product);
}

/**
 * Integer floor + clamp of a typed multiplier into the recipe-declared
 * `[bounds.min, bounds.max]` (CONTEXT C). Mirrors the reference HTML's
 * `parseAndClampTypedMultiplier`.
 *
 * - finite `raw` → `Math.floor(raw)` then clamp into `[min, max]`.
 * - non-finite `raw` (NaN / ±Infinity) → `undefined`, the sentinel the renderer
 *   treats as "revert to last-good" (Plan 01 PINS this as `undefined`; REND-08).
 */
export function clampMultiplier(raw: number, bounds: MultiplierBoundsLike): number | undefined {
  // Non-finite raw → the revert-to-last-good sentinel.
  if (!Number.isFinite(raw)) {
    return undefined;
  }

  const floored = Math.floor(raw);
  if (floored < bounds.min) {
    return bounds.min;
  }
  if (floored > bounds.max) {
    return bounds.max;
  }
  return floored;
}

/**
 * Effective "× base equivalent" the stepper sublabel renders (CONTEXT C).
 *
 * - primary-role → the multiplier itself.
 * - non-primary-role → multiplier + primaryStep.multiplier (the fractional
 *   additional base the primary step contributes).
 *
 * Returns `undefined` on a non-finite multiplier or step fraction (the renderer
 * shows "—").
 */
export function effectiveBaseEquivalent({
  primaryStep,
  multiplier,
  isPrimaryRole,
}: EffectiveBaseEquivalentArgs): number | undefined {
  // Entry guard: a non-finite multiplier bails for either role.
  if (!Number.isFinite(multiplier)) {
    return undefined;
  }

  if (isPrimaryRole) {
    return multiplier;
  }

  const primaryFraction = primaryStep.multiplier;
  if (!Number.isFinite(primaryFraction)) {
    return undefined;
  }

  const sum = multiplier + primaryFraction;
  // Post-add guard: MAX_VALUE + MAX_VALUE can overflow to Infinity.
  if (!Number.isFinite(sum)) {
    return undefined;
  }

  return sum;
}

/**
 * Effective multiplier of a predefined-series index (CONTEXT C):
 *   index × series.multiplierPerIndex.
 *
 * Returns `undefined` on a non-finite index, per-index factor, or product.
 */
export function seriesEffective(series: SeriesLike, index: number): number | undefined {
  const perIndex = series.multiplierPerIndex;

  // Entry guard: non-finite index or per-index factor → fallback.
  if (!Number.isFinite(index) || !Number.isFinite(perIndex)) {
    return undefined;
  }

  const product = index * perIndex;
  // Post-multiply guard: overflow → fallback.
  if (!Number.isFinite(product)) {
    return undefined;
  }

  return product;
}
